import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getEventById, registerForEvent } from '../services/event.service';
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';
import Header from '../components/HeaderThree';
import ScrollToTop from "react-scroll-to-top";
import Preloader from "../helper/Preloader";
import ColorInit from "../helper/ColorInit";
import NewsletterTwo from "../components/NewsletterTwo";

const EventDetailsPage = () => {
    const { id } = useParams();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({ name: '', phone: '', address: '' });
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        const fetchEvent = async () => {
            try {
                const data = await getEventById(id);
                setEvent(data);
            } catch (error) {
                console.error("Error fetching event details:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchEvent();
    }, [id]);

    const handleRegister = async (e) => {
        e.preventDefault();

        // Basic Validation
        if (!formData.name.trim()) {
            setMessage({ type: 'error', text: 'Please enter your full name.' });
            return;
        }

        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(formData.phone)) {
            setMessage({ type: 'error', text: 'Please enter a valid 10-digit phone number.' });
            return;
        }

        setSubmitting(true);
        setMessage(null);
        try {
            await registerForEvent({
                eventId: event.id,
                eventTitle: event.title,
                ...formData
            });
            setMessage({ type: 'success', text: 'Successfully registered for the event!' });
            setFormData({ name: '', phone: '', address: '' });
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to register. Please try again.' });
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <>
                <ColorInit color={true} />
                <Preloader />
                <Header />
                <div className="text-center py-80">
                    <div className="spinner-border text-main-600" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (!event) {
        return (
            <>
                <Header />
                <div className="text-center py-80">
                    <h3>Event not found</h3>
                    <Link to="/workshops" className="btn btn-main-600 mt-24 px-32 rounded-pill">Back to Workshops</Link>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <ColorInit color={true} />
            <Preloader />
            <ScrollToTop smooth color="var(--brand-primary)" />
            <Header />
            <Breadcrumb title={event.title} />

            <section className="event-details-section py-80">
                <div className="container">
                    <div className="row gy-5">
                        {/* Column 1: Image */}
                        <div className="col-lg-4">
                            <div className="event-image-sticky sticky-top" style={{ top: '160px', zIndex: 5 }}>
                                <div className="bg-white rounded-24 border border-gray-100 overflow-hidden shadow-sm">
                                    <img
                                        src={event.imageUrl || 'assets/images/thumbs/product-img1.png'}
                                        alt={event.title}
                                        className="w-100 h-400 object-fit-cover"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Column 2: Details */}
                        <div className="col-lg-4">
                            <div className="event-info-box h-100">
                                <h2 className="mb-24 fw-bold text-gray-900 leading-tight">{event.title}</h2>

                                <div className="d-flex flex-column gap-16 mb-32">
                                    <div className="d-flex align-items-center gap-16 p-20 bg-white rounded-20 border border-gray-100 shadow-xs">
                                        <div className="w-44 h-44 rounded-circle bg-main-50 text-main-600 flex-center shrink-0">
                                            <i className="ph ph-calendar text-2xl"></i>
                                        </div>
                                        <div>
                                            <p className="text-[11px] text-gray-400 font-bold uppercase mb-2 tracking-wider">Date</p>
                                            <p className="text-sm font-semibold text-gray-900">{formatDate(event.startAt).split(' at ')[0]}</p>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-16 p-20 bg-white rounded-20 border border-gray-100 shadow-xs">
                                        <div className="w-44 h-44 rounded-circle bg-main-50 text-main-600 flex-center shrink-0">
                                            <i className="ph ph-clock text-2xl"></i>
                                        </div>
                                        <div>
                                            <p className="text-[11px] text-gray-400 font-bold uppercase mb-2 tracking-wider">Time</p>
                                            <p className="text-sm font-semibold text-gray-900">{formatDate(event.startAt).split(' at ')[1]}</p>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-16 p-20 bg-white rounded-20 border border-gray-100 shadow-xs">
                                        <div className="w-44 h-44 rounded-circle bg-main-50 text-main-600 flex-center shrink-0">
                                            <i className="ph ph-map-pin text-2xl"></i>
                                        </div>
                                        <div>
                                            <p className="text-[11px] text-gray-400 font-bold uppercase mb-2 tracking-wider">Location</p>
                                            <p className="text-sm font-semibold text-gray-900 truncate max-w-200">
                                                {event.type === 'ONLINE' ? 'Online Meeting' : (event.venue || 'TBA')}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="description-area bg-white p-24 rounded-24 border border-gray-50 shadow-xs">
                                    <h6 className="mb-12 fw-bold text-sm">About Event</h6>
                                    <div className="text-gray-500 text-sm leading-relaxed">
                                        {event.description ? (
                                            <p className="mb-0">{event.description}</p>
                                        ) : (
                                            <p className="mb-0">Join us for an immersive artform experience where tradition meets creativity. This session offers a unique opportunity to engage with high-quality artistic expressions.</p>
                                        )}
                                    </div>
                                </div>

                                {event.type === 'ONLINE' && event.meetLink && (
                                    <div className="mt-24 p-16 bg-info-50 rounded-20 border border-info-100 d-flex align-items-center justify-between gap-12">
                                        <div className="d-flex align-items-center gap-10">
                                            <i className="ph-fill ph-video-camera text-24 text-info"></i>
                                            <div>
                                                <span className="text-xs fw-bold text-info d-block">Virtual Meet Link</span>
                                                {new Date(event.startAt).getTime() - new Date().getTime() > 3600000 ? (
                                                    <span className="text-[10px] text-gray-400">Available 1 hour before event</span>
                                                ) : (
                                                    <span className="text-[10px] text-green-600">Link is now active</span>
                                                )}
                                            </div>
                                        </div>
                                        {new Date(event.startAt).getTime() - new Date().getTime() > 3600000 ? (
                                            <button disabled className="btn btn-gray-200 text-gray-500 rounded-pill px-16 py-6 text-xs fw-bold opacity-50 cursor-not-allowed">Join Soon</button>
                                        ) : (
                                            <a href={event.meetLink.startsWith('http') ? event.meetLink : `https://${event.meetLink}`} target="_blank" rel="noreferrer" className="btn btn-info text-white rounded-pill px-16 py-6 text-xs fw-bold hover-bg-info-700 transition-2">Join Now</a>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Column 3: Form */}
                        <div className="col-lg-4">
                            <div className="sticky-top" style={{ top: '160px', zIndex: 5 }}>
                                <div className="bg-white rounded-24 border border-gray-100 p-20 shadow-sm" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                                    <h4 className="mb-20 fw-bold text-lg">Register Now</h4>
                                    <form onSubmit={handleRegister} className="row g-12">
                                        <div className="col-12">
                                            <label className="form-label text-[10px] font-bold text-gray-600 uppercase mb-2">Full Name *</label>
                                            <input
                                                type="text"
                                                className="form-control rounded-pill px-16 py-8 text-sm"
                                                placeholder="Name"
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label text-[10px] font-bold text-gray-600 uppercase mb-2">Phone *</label>
                                            <input
                                                type="tel"
                                                className="form-control rounded-pill px-16 py-8 text-sm"
                                                placeholder="10 digits"
                                                value={formData.phone}
                                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label text-[10px] font-bold text-gray-600 uppercase mb-2">Address</label>
                                            <textarea
                                                className="form-control rounded-16 px-16 py-8 text-sm"
                                                placeholder="Address (optional)"
                                                rows="2"
                                                value={formData.address}
                                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                            ></textarea>
                                        </div>

                                        {message && (
                                            <div className="col-12">
                                                <div className={`p-8 rounded-12 text-center text-[10px] fw-bold ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                    {message.text}
                                                </div>
                                            </div>
                                        )}

                                        <div className="col-12">
                                            <button
                                                type="submit"
                                                className="btn-main-600 w-100 rounded-pill bg-main-600 text-white py-12 mt-4 shadow-sm fw-bold text-sm"
                                                disabled={submitting}
                                            >
                                                {submitting ? 'BOOKING IN PROGRESS' : 'BOOK NOW'}
                                            </button>
                                        </div>
                                        <div className="col-12">
                                            <p className="text-[9px] text-gray-400 text-center mb-0">By registering, you agree to our terms.</p>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* NewsletterTwo */}
      <NewsletterTwo />
            <Footer />
        </>
    );
};

export default EventDetailsPage;